// Client-side "favorite contacts" — a device-local set of userIds surfaced as
// speed-dials on the Main screen. There is NO server API for favorites (they
// never leave the device), so this is deliberately synchronous localStorage,
// the same pattern as `shell/devServer.ts`: offline-first, favoriting must work
// with the backend unreachable, and a toggle should take effect without an
// async round-trip.
//
// A cached snapshot backs `getFavorites()` so it is referentially stable
// between writes — a hard requirement for `useSyncExternalStore` consumers.

const KEY = 'merkury.favorites';

type Listener = () => void;
const listeners = new Set<Listener>();

let cache: readonly string[] | undefined;

function ls(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    // Storage can throw (disabled cookies, sandboxed iframe) — treat as absent.
    return undefined;
  }
}

function load(): string[] {
  const raw = ls()?.getItem(KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function persist(ids: string[]): void {
  cache = ids;
  ls()?.setItem(KEY, JSON.stringify(ids));
  for (const listener of listeners) listener();
}

/** Subscribe to favorite-set changes (for `useSyncExternalStore`). */
export function subscribeFavorites(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The current favorite userIds. Referentially stable until the set changes. */
export function getFavorites(): readonly string[] {
  if (cache === undefined) cache = load();
  return cache;
}

export function isFavorite(userId: string): boolean {
  return getFavorites().includes(userId);
}

/** Add or remove `userId` from favorites; no-op if already in the wanted state. */
export function setFavorite(userId: string, favorite: boolean): void {
  const current = getFavorites();
  const has = current.includes(userId);
  if (favorite && !has) persist([...current, userId]);
  else if (!favorite && has) persist(current.filter((id) => id !== userId));
}

/** Flip `userId`'s favorite state; returns the new state. */
export function toggleFavorite(userId: string): boolean {
  const next = !isFavorite(userId);
  setFavorite(userId, next);
  return next;
}

/** Wipe all favorites — sign-out must not leave the previous account's stars. */
export function clearFavorites(): void {
  cache = [];
  ls()?.removeItem(KEY);
  for (const listener of listeners) listener();
}

/** Test seam: drop the in-memory snapshot so the next read re-hydrates. */
export function resetFavoritesCache(): void {
  cache = undefined;
}
